import React from "react";

import classnames from "classnames";

import { PlanHeader, FeatureBox, MonthlyCost, FeatureBoxProps } from "Cobalt/Plan/Card";
import { Card, CardSection } from "Common/Card/";
import { Div } from "Common/HTML";
// import { MoreDetailsButton, MedicalPlanDetailsModal } from "Common/plans/MedicalPlanDetailsModal";
// import PlanDetailsModal from "Common/plans/PlanDetailsModal";
import { SizeSwitch } from "Common/Responsive";

import type { MedicalPlanPrice } from "Utils/types";

type BasePlanCardProps = {
  plan: MedicalPlanPrice;
  isSampleQuote: boolean;
  planType?: "medical" | "dental" | "vision" | "life" | "short_term_disability" | "long_term_disability";
  featureBoxes: React.ReactNode;
  moreDetails: React.ReactNode;
};

const BasePlanCard = ({ plan, isSampleQuote, planType, featureBoxes, moreDetails }: BasePlanCardProps) => (
  <Card mb={planType === "medical" || plan.type === "medical" ? 0 : 4} p={0}>
    <CardSection className="flex justify-start items-center gap-4 h-[85px]">
      <PlanHeader plan={plan} />
      <SizeSwitch md={featureBoxes} />
      <Div className="ml-auto">
        <MonthlyCost cost={plan.total_cost} loading={false} hasFootnote={isSampleQuote} costType="total" />
      </Div>
    </CardSection>
    <SizeSwitch xs={<CardSection className="flex items-center gap-4">{featureBoxes}</CardSection>} md={React.Fragment} />
    <CardSection py={0} className="flex items-center justify-start gap-4 h-10">
      {moreDetails}
    </CardSection>
  </Card>
);

/**
 * Compoosible Components -- Start
 */

const BenAdminFeatureBox = ({ className, ...rest }: FeatureBoxProps) => (
  <FeatureBox className={classnames("md:min-w-[130px] cursor-auto", className)} {...rest} />
);

// FeatureBoxes
const MedicalFeatureBoxes = ({ plan }) => (
  <>
    <BenAdminFeatureBox plan={plan} title="Tier" field="metal_tier" format="capitalize" />
    <BenAdminFeatureBox plan={plan} title="Network Type" field="plan_type" format="acronym" />
    <BenAdminFeatureBox plan={plan} title="Deductible" field="deductible" format="currency" />
    <SizeSwitch md={<BenAdminFeatureBox plan={plan} title="Doctors Visits" field="estimated_costs.pcp_visit" format="currency" />} />
  </>
);

const DentalFeatureBoxes = ({ plan }) => (
  <>
    <BenAdminFeatureBox plan={plan} title="Max Benefit" field="annual_maximum" format="currency" />
    <BenAdminFeatureBox plan={plan} title="Deductible" field="deductible" format="currency" />
    <BenAdminFeatureBox plan={plan} title="Preventative" field="preventive_care" format="capitalize" />
    <SizeSwitch md={<BenAdminFeatureBox plan={plan} title="Network Type" field="plan_type" format="acronym" />} />
  </>
);

const VisionFeatureBoxes = ({ plan }) => (
  <>
    <BenAdminFeatureBox plan={plan} title="Eye Exam" field="eye_exam" />
    <BenAdminFeatureBox plan={plan} title="Lenses" field="lenses" format="html" />
    <BenAdminFeatureBox plan={plan} title="Contacts" field="contacts" format="html" />
  </>
);

const LifeFeatureBoxes = ({ plan }) => (
  <>
    <BenAdminFeatureBox plan={plan} title="Coverage" field="coverage_amount" format="currency" />
    <BenAdminFeatureBox plan={plan} title="Policy Type" field="plan_type" />
    <BenAdminFeatureBox plan={plan} title="Contribution" value="100%" />
  </>
);

const ShortTermDisabilityFeatureBoxes = ({ plan }) => {
  let durationBoxValue = plan.benefit_duration;
  if (durationBoxValue.includes("Social Security")) {
    durationBoxValue = "Retirement";
  }
  return (
    <>
      <BenAdminFeatureBox plan={plan} title="Coverage" value={`${plan.salary_coverage_percentage}% Salary`} />
      <BenAdminFeatureBox plan={plan} title="Weekly Max" value={plan.max_benefit.replace(/\s+per\s+\w+/i, "")} />
      <BenAdminFeatureBox plan={plan} title="Duration" value={durationBoxValue} />
    </>
  );
};

const LongTermDisabilityFeatureBoxes = ({ plan }) => (
  <>
    <BenAdminFeatureBox plan={plan} title="Coverage" value={`${plan.salary_coverage_percentage}% Salary`} />
    <BenAdminFeatureBox plan={plan} title="Monthly Max" value={plan.max_benefit.replace(/\s+per\s+\w+/i, "")} />
    <BenAdminFeatureBox plan={plan} title="Wait Period" field="elimination_period" />
  </>
);

// Plan Details
const MoreDetailsSBCLink = ({ plan }) => (
  <a href={plan.sbc_url} target="_blank" rel="noreferrer">
    View Plan Summary of Benefits and Coverage
  </a>
);

// const DefaultPlanDetails = ({ plan }) => (
//   <>
//     <MoreDetailsButton plan={plan} showIcon={false} buttonTextOverride="View Plan Details" />
//     <PlanDetailsModal plan={plan} inCart />
//   </>
// );
// const MedicalPlanDetails = ({ plan, isSampleQuote }) => (
//   <>
//     <MoreDetailsButton plan={plan} showIcon={false} buttonTextOverride="View Plan Details" />
//     <MedicalPlanDetailsModal plan={plan} isSampleQuote={isSampleQuote} inCart isAlternateCart />
//   </>
// );

/**
 * Composible Components -- End
 */

type ConcretePlanCardProps = BasePlanCardProps;

export const MedicalPlanCard = (props: ConcretePlanCardProps) => {
  const { plan } = props;
  return <BasePlanCard featureBoxes={<MedicalFeatureBoxes plan={plan} />} moreDetails={<MoreDetailsSBCLink plan={plan} />} {...props} />;
};

export const DentalPlanCard = (props: ConcretePlanCardProps) => {
  const { plan } = props;
  return <BasePlanCard featureBoxes={<DentalFeatureBoxes plan={plan} />} moreDetails={<MoreDetailsSBCLink plan={plan} />} {...props} />;
};

export const VisionPlanCard = (props: ConcretePlanCardProps) => {
  const { plan } = props;
  return <BasePlanCard featureBoxes={<VisionFeatureBoxes plan={plan} />} moreDetails={<MoreDetailsSBCLink plan={plan} />} {...props} />;
};

export const LifePlanCard = (props: ConcretePlanCardProps) => {
  const { plan } = props;
  return <BasePlanCard featureBoxes={<LifeFeatureBoxes plan={plan} />} moreDetails={<MoreDetailsSBCLink plan={plan} />} {...props} />;
};

export const ShortTermDisabilityPlanCard = (props: ConcretePlanCardProps) => {
  const { plan } = props;
  return <BasePlanCard featureBoxes={<ShortTermDisabilityFeatureBoxes plan={plan} />} moreDetails={<MoreDetailsSBCLink plan={plan} />} {...props} />;
};

export const LongTermDisabilityPlanCard = (props: ConcretePlanCardProps) => {
  const { plan } = props;
  return <BasePlanCard featureBoxes={<LongTermDisabilityFeatureBoxes plan={plan} />} moreDetails={<MoreDetailsSBCLink plan={plan} />} {...props} />;
};
